import {
  getCookieConsentCookieDomain,
  getCookieConsentCookieName,
  readExplicitCookieConsentCategory,
  resolveCookieConsentDeployment,
} from '@repo/common/constants/cookie-consent';
import {
  parseSignupAttributionQuery,
  SIGNUP_SOURCES,
  type SignupAttribution,
  type SignupSource,
} from '@repo/common/types/signup-attribution';

import { hasCookieConsent, hasGlobalPrivacyControl, subscribeToConsent } from '../contexts/cookie-consent-engine';
import { setCookie } from './cookie';
import { MARKETING_STORAGE_RETENTION_MS, SIGNUP_ATTRIBUTION_STORAGE_KEY } from './marketing-storage';

/**
 * `undecided` is not `denied`: before the consent engine has loaded, or while a visitor in an opt-in region has not
 * chosen, nothing may be stored or sent, but nothing already held may be destroyed either.
 */
type AttributionConsent = 'granted' | 'denied' | 'undecided';

function readAttributionConsent(): AttributionConsent {
  if (hasGlobalPrivacyControl()) return 'denied';
  const name = getCookieConsentCookieName(window.location.hostname);
  const raw = document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .find(cookie => cookie.startsWith(`${name}=`));
  if (raw) {
    try {
      return readExplicitCookieConsentCategory(decodeURIComponent(raw.slice(name.length + 1)), 'marketing') === true
        ? 'granted'
        : 'denied';
    } catch {
      return 'denied';
    }
  }
  return hasCookieConsent('marketing') ? 'granted' : 'undecided';
}

function hasAttributionConsent(): boolean {
  return readAttributionConsent() === 'granted';
}

const STORAGE_KEY = SIGNUP_ATTRIBUTION_STORAGE_KEY;
export type FirstTouchAttribution = SignupAttribution;

let firstTouch: FirstTouchAttribution | undefined;
/** A `signup_touch` read from the URL while consent was unknown. Memory only: never stored or sent before a grant. */
let pendingIncomingTouch: FirstTouchAttribution | undefined;
let capturedEpoch = 0;
let firstTouchCapturedAt = 0;

function signupCompletionEpoch(): number {
  const name = `starthub-signup-completed-${resolveCookieConsentDeployment(window.location.hostname)}`;
  const raw = document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .find(cookie => cookie.startsWith(`${name}=`));
  return Number(raw?.slice(name.length + 1)) || 0;
}

function readStoredAttribution(
  now: number,
  epoch: number,
): { attribution: FirstTouchAttribution; capturedAt: number } | undefined {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return undefined;
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== 'object' || parsed === null) {
    localStorage.removeItem(STORAGE_KEY);
    return undefined;
  }
  const storedEpoch: unknown = Reflect.get(parsed, 'capturedEpoch');
  const storedCapturedAt: unknown = Reflect.get(parsed, 'capturedAt');
  if (
    typeof storedEpoch !== 'number' ||
    storedEpoch < epoch ||
    typeof storedCapturedAt !== 'number' ||
    !Number.isFinite(storedCapturedAt) ||
    storedCapturedAt > now ||
    now - storedCapturedAt >= MARKETING_STORAGE_RETENTION_MS
  ) {
    localStorage.removeItem(STORAGE_KEY);
    return undefined;
  }
  const attribution = readAttribution(raw);
  return attribution ? { attribution, capturedAt: storedCapturedAt } : undefined;
}

export function clearSignupAttribution(): void {
  let hadPermittedAttribution = false;
  try {
    const now = Date.now();
    const epoch = signupCompletionEpoch();
    hadPermittedAttribution =
      hasCookieConsent('marketing') &&
      ((!!firstTouch &&
        firstTouchCapturedAt <= now &&
        now - firstTouchCapturedAt < MARKETING_STORAGE_RETENTION_MS &&
        capturedEpoch >= epoch) ||
        !!readStoredAttribution(now, epoch));
  } catch {
    hadPermittedAttribution = false;
  }
  firstTouch = undefined;
  firstTouchCapturedAt = 0;
  try {
    if (hadPermittedAttribution) {
      const name = `starthub-signup-completed-${resolveCookieConsentDeployment(window.location.hostname)}`;
      const domain = getCookieConsentCookieDomain(window.location.hostname);
      capturedEpoch = Math.max(Date.now(), signupCompletionEpoch() + 1);
      setCookie(name, String(capturedEpoch), {
        domain,
        maxAgeSeconds: 15_724_800,
        path: '/',
        sameSite: 'Lax',
        secure: domain !== undefined,
      });
    }
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Registration must still succeed when browser storage is blocked.
    return;
  }
}

export function getSignupAttributionSnapshot(): string {
  const now = Date.now();
  return typeof window !== 'undefined' &&
    hasAttributionConsent() &&
    firstTouch &&
    firstTouchCapturedAt <= now &&
    now - firstTouchCapturedAt < MARKETING_STORAGE_RETENTION_MS &&
    capturedEpoch >= signupCompletionEpoch()
    ? JSON.stringify(firstTouch)
    : '';
}

export function getServerSignupAttributionSnapshot(): string {
  return '';
}

export function subscribeToSignupAttribution(listener: () => void): () => void {
  const update = () => {
    captureSignupAttribution();
    listener();
  };
  const unsubscribe = subscribeToConsent(update);
  window.addEventListener('focus', update);
  window.addEventListener('pageshow', update);
  window.addEventListener('storage', update);
  // Capture only after commit, never while React reads a snapshot during render.
  update();
  return () => {
    unsubscribe();
    window.removeEventListener('focus', update);
    window.removeEventListener('pageshow', update);
    window.removeEventListener('storage', update);
  };
}

export function initializeSignupAttribution(): () => void {
  return subscribeToSignupAttribution(() => undefined);
}

function readAttribution(encoded: string | null): FirstTouchAttribution | undefined {
  if (!encoded) return undefined;
  try {
    return parseSignupAttributionQuery(encoded);
  } catch {
    return undefined;
  }
}

function classifySignupSource(url: URL, referrer: URL | undefined): SignupSource {
  const params = url.searchParams;
  const explicit = SIGNUP_SOURCES.find(value => value === params.get('signup_source'));
  if (explicit) return explicit;

  const medium = params.get('utm_medium')?.toLowerCase();
  const source = params.get('utm_source')?.toLowerCase();
  if (
    ['facebook', 'instagram', 'meta', 'fb', 'ig'].includes(source || '') &&
    ['paid', 'paid_social', 'cpc', 'ppc'].includes(medium || '')
  )
    return 'Meta Ad';
  if (medium === 'email') return 'Email';
  if (medium === 'organic') return 'Organic';
  if (medium === 'referral') return 'Referral';
  if (source || medium || params.has('utm_campaign')) return 'Unknown';
  if (referrer && referrer.origin !== url.origin) {
    if (/^(www\.)?(google\.(com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})|bing\.com)$/.test(referrer.hostname)) {
      return 'Organic';
    }
    return 'Referral';
  }
  return 'Direct';
}

export function captureSignupAttribution(): FirstTouchAttribution {
  const now = Date.now();
  const epoch = signupCompletionEpoch();
  const staleMemory =
    !!firstTouch &&
    (capturedEpoch < epoch ||
      firstTouchCapturedAt > now ||
      now - firstTouchCapturedAt >= MARKETING_STORAGE_RETENTION_MS);
  if (staleMemory) {
    firstTouch = undefined;
    firstTouchCapturedAt = 0;
  }
  const incomingUrl = new URL(window.location.href);
  const fromUrl = readAttribution(incomingUrl.searchParams.get('signup_touch'));
  if (incomingUrl.searchParams.has('signup_touch')) {
    incomingUrl.searchParams.delete('signup_touch');
    window.history.replaceState(window.history.state, '', incomingUrl);
  }
  if (fromUrl) pendingIncomingTouch = fromUrl;
  const consent = readAttributionConsent();
  if (consent === 'denied') {
    firstTouch = undefined;
    firstTouchCapturedAt = 0;
    pendingIncomingTouch = undefined;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      return { source: 'Unknown' };
    }
    return { source: 'Unknown' };
  }
  // Unknown consent releases nothing and destroys nothing: storage and a held handoff stay, only the memory copy
  // of an earlier touch is dropped (it is re-read from storage once consent is granted).
  if (consent === 'undecided') {
    firstTouch = undefined;
    firstTouchCapturedAt = 0;
    return { source: 'Unknown' };
  }
  const incoming = pendingIncomingTouch;
  pendingIncomingTouch = undefined;
  if (firstTouch) return { ...firstTouch };
  try {
    const stored = readStoredAttribution(now, epoch);
    if (stored) {
      firstTouch = stored.attribution;
      capturedEpoch = epoch;
      firstTouchCapturedAt = stored.capturedAt;
      return { ...stored.attribution };
    }
  } catch {
    // Storage can be blocked or contain an obsolete value; retain this visit in memory.
    firstTouch = undefined;
  }
  if (incoming) {
    firstTouch = incoming;
    capturedEpoch = epoch;
    firstTouchCapturedAt = now;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...incoming, capturedEpoch, capturedAt: firstTouchCapturedAt }),
      );
    } catch {
      return { ...incoming };
    }
    return { ...incoming };
  }
  const url = new URL(window.location.href);
  const params = url.searchParams;
  if (staleMemory) url.search = '';
  let referrer: URL | undefined;
  try {
    referrer = document.referrer ? new URL(document.referrer) : undefined;
  } catch {
    referrer = undefined;
  }
  firstTouch = {
    source: classifySignupSource(url, referrer),
    firstLandingPage: `${url.origin}${url.pathname}`,
    originalReferrer: referrer ? `${referrer.origin}${referrer.pathname}` : undefined,
  };
  capturedEpoch = epoch;
  firstTouchCapturedAt = now;
  const fields = {
    utmSource: 'utm_source',
    utmMedium: 'utm_medium',
    utmCampaign: 'utm_campaign',
    utmContent: 'utm_content',
    campaignId: 'campaign_id',
    adsetId: 'adset_id',
    adId: 'ad_id',
  } as const;
  for (const key of Object.keys(fields) as (keyof typeof fields)[]) {
    const value = params.get(fields[key]);
    if (value && value.length <= 256 && ![...value].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127))
      firstTouch[key] = value;
  }
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...firstTouch, capturedEpoch, capturedAt: firstTouchCapturedAt }),
    );
  } catch {
    return { ...firstTouch };
  }
  return { ...firstTouch };
}
